import type jsPDF from 'jspdf';
import { appendLandscapeMap, pdfSafe } from '../enterprise/enterpriseReportPdf';
import { physicalPageLabels } from '../enterprise/enterpriseReportLayout';
import type { TechnicalFlowReport, TechnicalPathRow } from './technicalFlowReport';

const BLUE: [number, number, number] = [30, 64, 110];
const INK: [number, number, number] = [30, 41, 59];
const MUTED: [number, number, number] = [100, 116, 139];
const LINE: [number, number, number] = [226, 232, 240];

interface DrawContext {
  pdf: jsPDF;
  margin: number;
  pageNumber: number;
  processName: string;
}

export interface FlowMapImage {
  dataUrl: string;
  widthPx: number;
  heightPx: number;
}

function addChrome(ctx: DrawContext, title: string) {
  const { pdf, margin } = ctx;
  const width = pdf.internal.pageSize.getWidth();
  const height = pdf.internal.pageSize.getHeight();
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...MUTED);
  pdf.text(ctx.processName, margin, 10);
  pdf.text(title, width - margin, 10, { align: 'right' });
  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.2);
  pdf.line(margin, 13, width - margin, 13);
  pdf.line(margin, height - 12, width - margin, height - 12);
  pdf.text('Documento técnico do fluxo', width - margin, height - 7, { align: 'right' });
}

function stampPageNumbers(pdf: jsPDF) {
  const labels = physicalPageLabels(pdf.getNumberOfPages());
  labels.forEach((label, index) => {
    pdf.setPage(index + 1);
    const height = pdf.internal.pageSize.getHeight();
    pdf.setFillColor(255, 255, 255);
    pdf.rect(12, height - 11, 28, 7, 'F');
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(label, 14, height - 7);
  });
}

function contentWidth(ctx: DrawContext): number {
  return ctx.pdf.internal.pageSize.getWidth() - ctx.margin * 2;
}

function ensureSpace(ctx: DrawContext, y: number, needed: number, title: string): number {
  const limit = ctx.pdf.internal.pageSize.getHeight() - 18;
  if (y + needed <= limit) return y;
  ctx.pdf.addPage();
  ctx.pageNumber += 1;
  addChrome(ctx, title);
  return 20;
}

function sectionTitle(ctx: DrawContext, y: number, text: string): number {
  ctx.pdf.setFont('helvetica', 'bold');
  ctx.pdf.setFontSize(12);
  ctx.pdf.setTextColor(...BLUE);
  ctx.pdf.text(text, ctx.margin, y);
  return y + 7;
}

function paragraph(ctx: DrawContext, y: number, text: string, title: string): number {
  const lines = ctx.pdf.splitTextToSize(pdfSafe(text), contentWidth(ctx));
  y = ensureSpace(ctx, y, lines.length * 4.2 + 2, title);
  ctx.pdf.setFont('helvetica', 'normal');
  ctx.pdf.setFontSize(9);
  ctx.pdf.setTextColor(...INK);
  ctx.pdf.text(lines, ctx.margin, y);
  return y + lines.length * 4.2 + 2;
}

async function drawTable(ctx: DrawContext, y: number, head: string[], body: string[][], title: string): Promise<number> {
  const { default: autoTable } = await import('jspdf-autotable');
  autoTable(ctx.pdf, {
    head: [head.map((cell) => pdfSafe(cell))],
    body: body.map((row) => row.map((cell) => pdfSafe(cell))),
    startY: y,
    margin: { left: ctx.margin, right: ctx.margin, bottom: 16 },
    showHead: 'everyPage',
    styles: { fontSize: 8, cellPadding: 1.4, overflow: 'linebreak', valign: 'middle', textColor: INK, lineColor: LINE, lineWidth: 0.1 },
    headStyles: { fillColor: BLUE, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) addChrome(ctx, title);
    },
  });
  return ((ctx.pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 8;
}

function pathBlocks(ctx: DrawContext, y: number, paths: TechnicalPathRow[], title: string): number {
  for (const path of paths) {
    y = ensureSpace(ctx, y, 16, title);
    ctx.pdf.setFont('helvetica', 'bold');
    ctx.pdf.setFontSize(9);
    ctx.pdf.setTextColor(...INK);
    ctx.pdf.text(pdfSafe(`${path.code} - ${path.title}`), ctx.margin, y);
    y += 4.5;
    y = paragraph(ctx, y, path.sequence, title);
    if (path.loopNote) y = paragraph(ctx, y, path.loopNote, title);
  }
  return y;
}

export async function renderTechnicalFlowPdf(
  model: TechnicalFlowReport,
  maps: { current: FlowMapImage | null; future: FlowMapImage | null },
  options?: { write?: (bytes: ArrayBuffer) => void },
): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const ctx: DrawContext = { pdf, margin: 14, pageNumber: 1, processName: model.processName };
  addChrome(ctx, 'Relatório Técnico do Fluxo');

  let y = 20;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(...INK);
  pdf.text('Relatório Técnico do Fluxo', ctx.margin, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text(pdfSafe(`${model.processName}   ·   ${model.generatedAt}   ·   ${model.organizationName ?? 'Organização não informada'}`), ctx.margin, y);
  y += 8;

  if (model.futureNote) y = paragraph(ctx, y, model.futureNote, 'Resumo');
  y = sectionTitle(ctx, y, 'Resumo comparativo');
  y = await drawTable(
    ctx,
    y,
    ['Indicador', 'Estado atual', 'Estado futuro', 'Variação'],
    model.indicators.map((row) => [row.indicator, row.current, row.future, row.variation]),
    'Resumo',
  );
  y = paragraph(ctx, y, 'O estado futuro, quando existe, é projeção. A variação não é resultado realizado.', 'Resumo');

  y = sectionTitle(ctx, y, 'Estado atual');
  if (maps.current && maps.current.widthPx > 0) {
    await appendLandscapeMap(pdf, ctx, maps.current, {
      title: 'Estado atual',
      caption: 'TP = permanência da atividade. TRA = tempo de realização.',
    });
    pdf.addPage('a4', 'portrait');
    ctx.pageNumber += 1;
    addChrome(ctx, 'Atividades');
    y = 20;
  } else {
    y = paragraph(ctx, y, 'Imagem do mapa não capturada nesta exportação.', 'Mapa');
  }

  y = sectionTitle(ctx, y, 'Estado futuro');
  if (!model.futureAvailable) {
    y = paragraph(ctx, y, model.futureNote ?? 'Estado futuro não disponível.', 'Mapa');
  } else if (maps.future && maps.future.widthPx > 0) {
    await appendLandscapeMap(pdf, ctx, maps.future, {
      title: 'Estado futuro',
      caption: 'TP = permanência da atividade. TRA = tempo de realização. Desenho projetado.',
    });
    pdf.addPage('a4', 'portrait');
    ctx.pageNumber += 1;
    addChrome(ctx, 'Atividades');
    y = 20;
  } else {
    y = paragraph(ctx, y, 'Imagem do estado futuro não capturada nesta exportação. Os números usam o estado futuro salvo.', 'Mapa');
  }

  y = sectionTitle(ctx, y, 'Atividades do estado atual');
  y = paragraph(ctx, y, model.legend, 'Atividades');
  y = await drawTable(
    ctx,
    y,
    ['#', 'Atividade', 'Responsável', 'Probabilidade de passagem', 'TP', 'TRA', 'Espera'],
    model.currentActivities.map((row) => [row.code, row.name, row.owner, row.probability, row.tp, row.tra, row.wait]),
    'Atividades',
  );

  if (model.futureAvailable) {
    y = sectionTitle(ctx, y, 'Atividades do estado futuro');
    y = paragraph(ctx, y, model.legend, 'Atividades');
    y = await drawTable(
      ctx,
      y,
      ['#', 'Atividade', 'Responsável', 'Probabilidade de passagem', 'TP', 'TRA', 'Espera'],
      model.futureActivities.map((row) => [row.code, row.name, row.owner, row.probability, row.tp, row.tra, row.wait]),
      'Atividades',
    );
  }

  y = sectionTitle(ctx, y, 'Caminhos do fluxo');
  y = pathBlocks(ctx, y, model.currentPaths, 'Caminhos');
  if (model.pathsTruncated) y = paragraph(ctx, y, 'A listagem parou no limite de caminhos. O grafo restante não foi estimado.', 'Caminhos');
  y = await drawTable(
    ctx,
    y,
    ['Caminho', 'Probabilidade', 'TP total', 'TRA total', 'Espera total', 'PCE'],
    model.currentPaths.map((path) => [`${path.code} - ${path.title}`, path.probability, path.tp, path.tra, path.wait, path.pce]),
    'Caminhos',
  );

  if (model.futureAvailable) {
    y = sectionTitle(ctx, y, 'Caminhos do estado futuro');
    y = pathBlocks(ctx, y, model.futurePaths, 'Caminhos');
    y = await drawTable(
      ctx,
      y,
      ['Caminho', 'Probabilidade', 'TP total', 'TRA total', 'Espera total', 'PCE'],
      model.futurePaths.map((path) => [`${path.code} - ${path.title}`, path.probability, path.tp, path.tra, path.wait, path.pce]),
      'Caminhos',
    );
    y = sectionTitle(ctx, y, 'Comparação dos caminhos');
    y = await drawTable(
      ctx,
      y,
      ['Caminho', 'TP atual', 'TRA atual', 'TP futuro', 'TRA futuro', 'Variação TP'],
      model.pathComparison.map((row) => [row.path, row.tpCurrent, row.traCurrent, row.tpFuture, row.traFuture, row.variation]),
      'Comparação',
    );
  }

  stampPageNumbers(pdf);
  if (options?.write) {
    options.write(pdf.output('arraybuffer'));
    return;
  }
  const fileName = `${model.processName.replace(/[^a-z0-9]/gi, '_')}_Relatorio_Tecnico_do_Fluxo.pdf`;
  pdf.save(fileName);
}
